import { ETableStructureType } from 'ui/EasTable/types';
import { TBoxStructure } from '../../../EasParamsBox/types';
import { maskSecret } from './utils';
import {
	ETypeOfGoods,
	TYPE_OF_GOODS_FILTER,
	WEIGHT_UNIT_FILTER,
} from '../EditShopifyStoreDialog/types';

export type TShopifyStoreFlat = {
	id: number;
	// Store
	store_id: number | null;
	store_url: string | null;
	store_front_url: string | null;
	name: string | null;
	blocked_at: string | null;
	disabled_at: string | null;
	// Application
	enabled: boolean | null;
	enabled_at: string | null;
	discounts: boolean | null;
	export_period: number | null;
	export_enabled: boolean | null;
	export_updated_at: string | null;
	// EAS Auth
	eas_is_sandbox: boolean | null;
	eas_id: string | null;
	eas_url: string | null;
	eas_secret: string | null;
	eas_id_sandbox: string | null;
	eas_url_sandbox: string | null;
	eas_secret_sandbox: string | null;
	// EAS Params
	immediately_process_paid_orders: boolean | null;
	weight_unit: string | null;
	act_as_disclosed_agent: boolean | null;
	hs6p_received: string | null;
	type_of_goods: ETypeOfGoods | null;
	reduced_tbe_vat_group: boolean | null;
	seller_registration_country: string | null;
	originating_country: string | null;
	location_warehouse_country: string | null;
	order_tags_enable: boolean | null;
	handle_ireland_as_ioss: boolean | null;
	swisspost_full_ddp_delivery_customs_description: boolean | null;
	// Sales Channels
	sales_channels_enabled: boolean | null;
	sales_channels_selected_will_be_processed: number[] | null;
	sales_channels_selected_titles: string[] | null;
	shipping_integration_enabled: boolean | null;
};

export const storeBoxStructure: TBoxStructure<TShopifyStoreFlat> = {
	title: 'Store Details',
	header: [],
	body: [
		{ name: 'Store ID', selector: 'store_id', type: ETableStructureType.NUMBER },
		{ name: 'Store URL', selector: 'store_url', type: ETableStructureType.COPYABLE_TEXT },
		{
			name: 'Storefront URL',
			selector: 'store_front_url',
			type: ETableStructureType.COPYABLE_TEXT,
		},
		{ name: 'Name', selector: 'name', type: ETableStructureType.STRING },
		{ name: 'Blocked At', selector: 'blocked_at', type: ETableStructureType.DATE_TIME },
		{ name: 'Disabled At', selector: 'disabled_at', type: ETableStructureType.DATE_TIME },
	],
};

export const applicationBoxStructure: TBoxStructure<TShopifyStoreFlat> = {
	title: 'Application status',
	header: [],
	body: [
		{ name: 'Application enabled', selector: 'enabled', type: ETableStructureType.BOOLEAN },
		{ name: 'Enabled At', selector: 'enabled_at', type: ETableStructureType.DATE_TIME },
		{ name: 'Discounts', selector: 'discounts', type: ETableStructureType.BOOLEAN },
		{
			name: 'Order Export Period, minutes',
			selector: 'export_period',
			type: ETableStructureType.NUMBER,
		},
		{
			name: 'Order Export Enabled',
			selector: 'export_enabled',
			type: ETableStructureType.BOOLEAN,
		},
		{
			name: 'Last Order Synchronisation At',
			selector: 'export_updated_at',
			type: ETableStructureType.DATE_TIME,
		},
	],
};

export const easAuthBoxStructure: TBoxStructure<TShopifyStoreFlat> = {
	title: 'Authorisation Settings',
	header: [],
	body: [
		{ name: 'Is Sandbox', selector: 'eas_is_sandbox', type: ETableStructureType.BOOLEAN },
		{ name: 'Client ID', selector: 'eas_id', type: ETableStructureType.COPYABLE_TEXT },
		{
			name: 'Secret',
			selector: 'eas_secret',
			type: ETableStructureType.COPYABLE_TEXT,
			// value: (item) => maskSecret(item.eas_secret),
		},
		{
			name: 'Sandbox Client ID',
			selector: 'eas_id_sandbox',
			type: ETableStructureType.COPYABLE_TEXT,
		},
		{
			name: 'Sandbox Secret',
			selector: 'eas_secret_sandbox',
			type: ETableStructureType.COPYABLE_TEXT,
			// value: (item) => maskSecret(item.eas_secret_sandbox),
		},
	],
};

export const easParametersBoxStructure: TBoxStructure<TShopifyStoreFlat> = {
	title: 'Application Parameters',
	header: [],
	body: [
		{
			name: 'Immediately process paid orders',
			selector: 'immediately_process_paid_orders',
			type: ETableStructureType.BOOLEAN,
		},
		{
			name: 'Weight units',
			selector: 'weight_unit',
			type: ETableStructureType.STATUS,
			filter: WEIGHT_UNIT_FILTER,
		},
		{
			name: 'Act as disclose agent',
			selector: 'act_as_disclosed_agent',
			type: ETableStructureType.BOOLEAN,
		},
		{ name: 'Default HS code', selector: 'hs6p_received', type: ETableStructureType.STRING },
		{
			name: 'Default product type',
			selector: 'type_of_goods',
			type: ETableStructureType.STATUS,
			filter: TYPE_OF_GOODS_FILTER,
		},
		{
			name: 'Default Reduced tbe vat group',
			selector: 'reduced_tbe_vat_group',
			type: ETableStructureType.BOOLEAN,
		},
		{
			name: 'Default Seller registration country',
			selector: 'seller_registration_country',
			type: ETableStructureType.COUNTRY,
		},
		{
			name: 'Default Origin Country',
			selector: 'originating_country',
			type: ETableStructureType.COUNTRY,
		},
		{
			name: 'Location Warehouse Country',
			selector: 'location_warehouse_country',
			type: ETableStructureType.COUNTRY,
		},
		{
			name: 'Add EAS tags to the order',
			selector: 'order_tags_enable',
			type: ETableStructureType.BOOLEAN,
		},
		{
			name: 'Handle Northern Ireland as IOSS/OSS',
			selector: 'handle_ireland_as_ioss',
			type: ETableStructureType.BOOLEAN,
		},
		{
			name: 'Add customs description meta (Swiss post)',
			selector: 'swisspost_full_ddp_delivery_customs_description',
			type: ETableStructureType.BOOLEAN,
		},
	],
};

export const salesChannelsBoxStructure: TBoxStructure<TShopifyStoreFlat> = {
	title: 'Sales Channels Configuration',
	banner: {
		message:
			'When sales channel override enabled, then orders from selected channel will be processed. By default orders from most of sales channels are skipped.',
		type: 'warning',
	},

	header: [],
	body: [
		{
			name: 'Override skipped sales channels logic',
			selector: 'sales_channels_enabled',
			type: ETableStructureType.BOOLEAN,
		},
		{
			name: 'Process orders for selected channels',
			selector: 'sales_channels_selected_titles',
			type: ETableStructureType.STATUS_ARRAY_VERTICAL,
		},
		{
			name: 'Shipping integration enabled',
			selector: 'shipping_integration_enabled',
			type: ETableStructureType.BOOLEAN,
		},
	],
};
